import { useEffect, useState, useRef } from "react";
import { X, Bookmark, Loader2, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { useWishlist } from "@/hooks/useWishlist";
import { fetchProductByHandle, type ShopifyProduct } from "@/lib/shopify";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useCartStore } from "@/stores/useCart";
import { lockScroll, unlockScroll } from "@/lib/scroll-lock";
import { toast } from "sonner";

interface WishlistDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenAccount: () => void;
}

const WishlistDrawer = ({ isOpen, onClose, onOpenAccount }: WishlistDrawerProps) => {
  const { handles, loading: wishlistLoading, authLoading, isLoggedIn, toggle } = useWishlist();
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [addingAll, setAddingAll] = useState(false);
  const [addingHandle, setAddingHandle] = useState<string | null>(null);
  const { formatPrice } = useCurrency();
  const prevHandlesRef = useRef<string>("");
  const addItem = useCartStore((s) => s.addItem);
  const cartLoading = useCartStore((s) => s.isLoading);

  useEffect(() => {
    if (isOpen) {
      lockScroll();
    } else {
      unlockScroll();
    }
    return () => { unlockScroll(); };
  }, [isOpen]);

  useEffect(() => {
    if (!isLoggedIn || handles.length === 0) {
      setProducts([]);
      prevHandlesRef.current = "";
      return;
    }

    const key = handles.join(",");
    if (key === prevHandlesRef.current) return;
    prevHandlesRef.current = key;

    let cancelled = false;
    const load = async () => {
      setLoadingProducts(true);
      const results = await Promise.all(handles.map((h) => fetchProductByHandle(h)));
      if (!cancelled) {
        setProducts(results.filter(Boolean) as ShopifyProduct[]);
        setLoadingProducts(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isLoggedIn, handles]);

  const handleAddToCart = async (product: ShopifyProduct) => {
    const variant = product.variants.edges[0]?.node;
    if (!variant) return;
    setAddingHandle(product.handle);
    await addItem({
      productTitle: product.title,
      productHandle: product.handle,
      productImage: product.images.edges[0]?.node.url || null,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
    });
    setAddingHandle(null);
    toast.success("Added to cart", { position: "top-center" });
  };

  const handleAddAllToCart = async () => {
    setAddingAll(true);
    for (const product of products) {
      const variant = product.variants.edges[0]?.node;
      if (!variant) continue;
      await addItem({
        productTitle: product.title,
        productHandle: product.handle,
        productImage: product.images.edges[0]?.node.url || null,
        variantId: variant.id,
        variantTitle: variant.title,
        price: variant.price,
        quantity: 1,
      });
    }
    setAddingAll(false);
    toast.success(`Added ${products.length} items to cart`, { position: "top-center" });
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[60] bg-background/60 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      <div
        className={`fixed top-0 right-0 bottom-0 z-[70] w-full max-w-[420px] bg-background border-l border-border flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 border-b border-border" style={{ height: "var(--header-height)" }}>
          <h2 className="heading-small text-foreground">Wishlist ({handles.length})</h2>
          <button onClick={onClose} className="text-foreground hover:text-accent transition-colors" aria-label="Close">
            <X size={20} strokeWidth={1.4} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {authLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
          ) : !isLoggedIn ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
              <Bookmark size={40} strokeWidth={1} className="text-muted-foreground" />
              <p className="body-medium text-muted-foreground">Sign in to save your favorite pieces</p>
              <button
                onClick={() => { onClose(); onOpenAccount(); }}
                className="btn h-10 px-6 bg-accent text-accent-foreground hover:bg-accent-hover transition-colors"
              >
                Sign In
              </button>
            </div>
          ) : wishlistLoading || loadingProducts ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Bookmark size={40} strokeWidth={1} className="text-muted-foreground mb-4" />
              <p className="body-medium text-muted-foreground">No saved pieces yet</p>
            </div>
          ) : (
            <div className="space-y-1">
              {products.map((product, index) => {
                const img = product.images.edges[0]?.node;
                const price = formatPrice(
                  product.priceRange.minVariantPrice.amount,
                  product.priceRange.minVariantPrice.currencyCode
                );
                const isAdding = addingHandle === product.handle;
                return (
                  <div
                    key={product.id}
                    className="flex gap-3 items-center py-3 animate-catalog-fade-in"
                    style={{
                      animationDelay: `${index * 90}ms`,
                      animationFillMode: "forwards",
                    }}
                  >
                    <Link to={`/product/${product.handle}`} onClick={onClose} className="flex gap-3 items-center flex-1 min-w-0">
                      <div className="w-16 h-16 flex-shrink-0 overflow-hidden bg-muted">
                        {img && <img src={img.url} alt={img.altText || product.title} className="w-full h-full object-cover" />}
                      </div>
                      <div className="min-w-0">
                        <p className="body-small text-foreground truncate">{product.title}</p>
                        <p className="body-small text-muted-foreground">{price}</p>
                      </div>
                    </Link>
                    <button
                      onClick={() => handleAddToCart(product)}
                      disabled={isAdding || cartLoading}
                      className="flex-shrink-0 w-9 h-9 border border-border flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-50"
                      aria-label="Add to cart"
                    >
                      {isAdding ? (
                        <Loader2 size={14} className="animate-spin text-muted-foreground" />
                      ) : (
                        <ShoppingBag size={15} strokeWidth={1.4} className="text-foreground" />
                      )}
                    </button>
                    <button
                      onClick={() => toggle(product.handle)}
                      className="flex-shrink-0 text-accent hover:text-foreground transition-colors"
                      aria-label="Remove from wishlist"
                    >
                      <Bookmark size={18} strokeWidth={1.3} fill="currentColor" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Fixed bottom: Add All to Cart */}
        {isLoggedIn && products.length > 0 && (
          <div className="border-t border-border px-6 py-4">
            <button
              onClick={handleAddAllToCart}
              disabled={addingAll || cartLoading}
              className="btn w-full h-12 bg-accent text-accent-foreground hover:bg-accent-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {addingAll ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  <ShoppingBag size={16} strokeWidth={1.4} />
                  Add All to Cart
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default WishlistDrawer;
